// useSharedRequest — read a shared request snapshot from window.location.hash
// (encoded by `toShareableUrl`), and expose a `consume()` helper to strip the
// hash from the URL once the form has been pre-filled.
//
// The hook is intentionally one-shot: it only reads the hash on initial mount
// (and only returns a snapshot whose `operationId` matches the current page).
// This keeps it idempotent under React StrictMode's double-effect.

import { useCallback, useRef, useState } from 'react';
import { decodeShareableHash, type ShareableState } from '@/lib/curl';

export interface UseSharedRequestResult {
    /** Decoded snapshot if URL hash carries one for this operation; else null. */
    sharedState: ShareableState | null;
    /** Strip `#try=…` from the URL bar (call after applying the snapshot). */
    consume: () => void;
}

export function useSharedRequest(operationId: string): UseSharedRequestResult {
    // Capture once: snapshot the initial state at mount and never recompute.
    // useState lazy initialiser runs exactly once per component instance —
    // even under StrictMode double-invocation in dev, the second call sees the
    // same memoised value, so behaviour stays idempotent.
    const [sharedState] = useState<ShareableState | null>(() => {
        if (typeof window === 'undefined') return null;
        const decoded = decodeShareableHash(window.location.hash || '');
        if (!decoded) return null;
        if (decoded.operationId !== operationId) return null;
        return decoded;
    });

    // Guard `consume` so repeated calls (e.g. dev double-render) are a no-op.
    const consumedRef = useRef(false);

    const consume = useCallback(() => {
        if (consumedRef.current) return;
        consumedRef.current = true;
        if (typeof window === 'undefined') return;
        try {
            const cleanUrl = window.location.pathname + window.location.search;
            window.history.replaceState(null, '', cleanUrl);
        } catch {
            // history.replaceState may throw in some sandboxed environments —
            // silently ignore; the form is already populated regardless.
        }
    }, []);

    return { sharedState, consume };
}
